// Site-wide presence client (§4.1): a single self-healing socket to
// `/ws/session`, opened on sign-in. It tracks who's online and dispatches raw
// session messages (presence deltas now; Ready-signals in Phase C) to
// subscribers. One socket per tab; screens subscribe/unsubscribe as they mount.

import { reportSocket } from "./connState.ts";
import { clientInstance, socketLimitMessage } from "./clientInstance.ts";
import { showSocketNotice } from "./ui/serverStatus.ts";

export interface PresenceUser {
  id: string;
  username: string;
}

/** A raw server→client session message (mirrors `presence::SessionMsg`). */
export type SessionMsg =
  | { t: "presence"; d: { online: PresenceUser[] } }
  | { t: "online"; d: PresenceUser }
  | { t: "offline"; d: { id: string } }
  | { t: "notification"; d: NotificationEnvelope }
  // Transitional decoder for a stale server/assets overlap during deployment.
  | { t: "ready"; d: ReadySignal }
  | { t: "pong" };

export type SemanticActionKind =
  | "open_url"
  | "accept_match"
  | "decline_match"
  | "open_game";

export interface PortableAction {
  kind: SemanticActionKind;
  label: string;
  url?: string | null;
  resource_id?: string | null;
  callback_id?: string | null;
}

export interface NotificationContext {
  from?: string | null;
  from_name?: string | null;
  challenge_id?: string | null;
  time_control?: string | null;
  game_id?: string | null;
  note?: string | null;
  link?: string | null;
}

export interface NotificationEnvelope {
  notification_id: string;
  kind: string;
  title: string;
  body: string;
  context: NotificationContext;
  actions: PortableAction[];
}

/** Transitional shape used by the old `ready` session message and by the
 * ready-banner adapter while stale assets can coexist. */
export interface ReadySignal {
  from: string; // sender user id
  from_name: string;
  kind: "ready" | "challenge" | "rematch" | "accepted" | "waiting" | "wait" | "later" | "declined";
  challenge_id: string;
  game_id?: string | null;
  note?: string | null;
  link: string; // where "Join"/CTA should go
}

const PING_EVERY_MS = 20000;
const RECONNECT_BASE_MS = 500;
const RECONNECT_MAX_MS = 8000;

let ws: WebSocket | undefined;
let closed = true;
let reconnectAttempts = 0;
let pingTimer: number | undefined;

const online = new Map<string, string>(); // id → username
const presenceListeners = new Set<() => void>();
const messageListeners = new Set<(msg: SessionMsg) => void>();

/** Open the presence socket (idempotent). Call once the member is signed in. */
export function startPresence(): void {
  if (!closed && ws) return;
  closed = false;
  connect();
}

/** Close the presence socket and clear state (call on sign-out). */
export function stopPresence(): void {
  closed = true;
  stopPing();
  ws?.close();
  ws = undefined;
  online.clear();
  emitPresence();
  reportSocket("open"); // intentional close, not a failure — clear the status bar
}

/** Is this user id currently online (has a live socket)? */
export function isOnline(id: string): boolean {
  return online.has(id);
}

/** Snapshot of everyone currently online. */
export function onlineUsers(): PresenceUser[] {
  return [...online.entries()].map(([id, username]) => ({ id, username }));
}

/** Subscribe to presence changes; returns an unsubscribe fn. */
export function onPresence(cb: () => void): () => void {
  presenceListeners.add(cb);
  return () => presenceListeners.delete(cb);
}

/** Subscribe to raw session messages (e.g. Ready-signals); returns unsubscribe. */
export function onSessionMessage(cb: (msg: SessionMsg) => void): () => void {
  messageListeners.add(cb);
  return () => messageListeners.delete(cb);
}

/** A banner calls this only once it is visibly rendered. Hidden documents must
 * not suppress the gateway's external fallback. */
export function acknowledgeNotification(notificationId: string): void {
  if (document.visibilityState !== "visible") return;
  if (ws?.readyState !== WebSocket.OPEN) return;
  ws.send(
    JSON.stringify({
      t: "notification-ack",
      d: { notification_id: notificationId },
    }),
  );
}

function connect(): void {
  const proto = location.protocol === "https:" ? "wss" : "ws";
  const url = `${proto}://${location.host}/ws/session?client_id=${clientInstance()}`;
  const sock = new WebSocket(url);
  ws = sock;

  sock.onopen = () => {
    if (ws !== sock) return;
    reconnectAttempts = 0;
    startPing();
    reportSocket("open"); // healthy socket ⇒ server reachable (plan item 4)
  };

  sock.onmessage = (ev) => {
    if (ws !== sock) return;
    let msg: SessionMsg;
    try {
      msg = JSON.parse(ev.data as string) as SessionMsg;
    } catch {
      return;
    }
    handle(msg);
  };

  sock.onclose = (event) => {
    if (ws !== sock) return;
    stopPing();
    const notice = socketLimitMessage(event?.code);
    if (notice) {
      closed = true;
      online.clear(); emitPresence(); reportSocket("open");
      showSocketNotice(notice, startPresence);
    }
    if (!closed) scheduleReconnect();
  };
  sock.onerror = () => sock.close();
}

function handle(msg: SessionMsg): void {
  switch (msg.t) {
    case "presence":
      online.clear();
      for (const u of msg.d.online) online.set(u.id, u.username);
      emitPresence();
      break;
    case "online":
      online.set(msg.d.id, msg.d.username);
      emitPresence();
      break;
    case "offline":
      online.delete(msg.d.id);
      emitPresence();
      break;
    case "pong":
      return;
  }
  // Fan every message (including ready) to raw subscribers.
  for (const cb of messageListeners) cb(msg);
}

function emitPresence(): void {
  for (const cb of presenceListeners) cb();
}

function startPing(): void {
  stopPing();
  pingTimer = window.setInterval(() => {
    if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ t: "ping" }));
  }, PING_EVERY_MS);
}
function stopPing(): void {
  if (pingTimer) clearInterval(pingTimer);
  pingTimer = undefined;
}

function scheduleReconnect(): void {
  reportSocket("reconnecting"); // sustained retries surface the status bar (item 4)
  const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** reconnectAttempts);
  reconnectAttempts++;
  const previous = ws;
  window.setTimeout(() => {
    if (!closed && ws === previous) connect();
  }, delay);
}
